import { toast } from '@aus-platform/design-system';
import DXFWriter from 'dxf-writer';
import FileSaver from 'file-saver';
import { isEmpty, isNil } from 'lodash';
import type { WorkspaceLayer } from 'src/map-3d/components/map-3d-sidebar';
import { PointShape } from 'src/swipe-map-3d/enums';
import { getPointCloudProperties } from '../../../map-3d-workspace/shared/helpers/point-cloud-helpers';
import { DxfAxisLabelAlignment } from '../../enums';
import { axisProperties, dxfConstants, rgbBitShift } from './constants';
import type {
  CrossSectionPointData,
  DataBounds,
  DxfLayerPoint,
  DxfPoint,
} from './types';

/**
 * Converts RGB bytes to DXF true_color integer format (0x00RRGGBB).
 * @param r - Red component (0-255).
 * @param g - Green component (0-255).
 * @param b - Blue component (0-255).
 * @returns DXF true_color integer.
 */
const rgbToDxfColor = (r: number, g: number, b: number): number => {
  const { red, green, blue } = rgbBitShift;
  return (r << red) | (g << green) | (b << blue);
};

/**
 * Groups points by their RGB color.
 * @param points - Array of points with x, y, r, g, b
 * @returns Map of color (as string key) to array of points
 */
const groupPointsByColor = (
  points: DxfPoint[],
): Map<string, DxfLayerPoint[]> => {
  const colorMap = new Map<string, DxfLayerPoint[]>();

  for (const point of points) {
    const colorKey = `${point.r},${point.g},${point.b}`;
    const existingPoints = colorMap.get(colorKey);

    if (isNil(existingPoints)) {
      colorMap.set(colorKey, []);
    }

    const pointGroup = colorMap.get(colorKey);
    if (!isNil(pointGroup)) {
      pointGroup.push({ x: point.x, y: point.y });
    }
  }

  return colorMap;
};

/**
 * Extracts points with colors and calculates data bounds.
 * @param data - Cross-section point data
 * @returns Object containing points array and bounds
 */
const extractPointsAndBounds = (
  data: CrossSectionPointData,
): { points: DxfPoint[]; bounds: DataBounds } => {
  const points: DxfPoint[] = [];
  const bounds: DataBounds = {
    minX: Infinity,
    maxX: -Infinity,
    minY: Infinity,
    maxY: -Infinity,
  };

  const {
    positionComponentsPerPoint,
    positionYOffset,
    colorComponentsPerPoint,
  } = dxfConstants;

  for (let i = 0; i < data.count; i++) {
    const posIdx = i * positionComponentsPerPoint;
    const colorIdx = i * colorComponentsPerPoint;
    const x = data.positions[posIdx];
    const y = data.positions[posIdx + positionYOffset];
    const r = data.colors[colorIdx];
    const g = data.colors[colorIdx + 1];
    const b = data.colors[colorIdx + 2];

    points.push({ x, y, r, g, b });

    bounds.minX = Math.min(bounds.minX, x);
    bounds.maxX = Math.max(bounds.maxX, x);
    bounds.minY = Math.min(bounds.minY, y);
    bounds.maxY = Math.max(bounds.maxY, y);
  }

  return { points, bounds };
};

/**
 * Creates and configures the gridlines layer in DXF.
 * @param dxf - DXF writer instance
 */
const createGridlinesLayer = (dxf: DXFWriter): void => {
  const {
    gridlineLayerName,
    gridlineLayerColor,
    gridlineLineType,
    gridlineColor,
  } = dxfConstants;

  const gridlineLayer = dxf.addLayer(
    gridlineLayerName,
    gridlineLayerColor,
    gridlineLineType,
  );
  dxf.setActiveLayer(gridlineLayerName);
  gridlineLayer.setTrueColor(gridlineColor);
};

/**
 * Draws gridlines and labels on the DXF.
 * @param dxf - DXF writer instance
 * @param xAxisStart - X-axis start position
 * @param xAxisEnd - X-axis end position
 * @param yAxisStart - Y-axis start position
 * @param yAxisEnd - Y-axis end position
 */
const drawGridlinesAndLabels = (
  dxf: DXFWriter,
  xAxisStart: number,
  xAxisEnd: number,
  yAxisStart: number,
  yAxisEnd: number,
): void => {
  const {
    gridlineCount,
    gridlineWidth,
    tickTextSize,
    tickDecimalPlaces,
    tickLabelOffsetX,
    tickLabelOffsetY,
  } = dxfConstants;

  const xStep = (xAxisEnd - xAxisStart) / gridlineCount;
  const yStep = (yAxisEnd - yAxisStart) / gridlineCount;

  for (let i = 0; i <= gridlineCount; i++) {
    const x = xAxisStart + i * xStep;
    const y = yAxisStart + i * yStep;

    // Horizontal gridlines.
    dxf.drawText(
      xAxisStart + tickLabelOffsetX,
      y,
      tickTextSize,
      0,
      `${y.toFixed(tickDecimalPlaces)}m`,
    );
    dxf.drawPolyline(
      [
        [xAxisStart, y],
        [xAxisEnd, y],
      ],
      false,
      gridlineWidth,
    );

    // Vertical gridlines.
    dxf.drawText(
      x,
      yAxisStart + tickLabelOffsetY,
      tickTextSize,
      0,
      `${x.toFixed(tickDecimalPlaces)}m`,
    );
    dxf.drawPolyline(
      [
        [x, yAxisStart],
        [x, yAxisEnd],
      ],
      false,
      gridlineWidth,
    );
  }
};

/**
 * Draws axis labels on the DXF.
 * @param dxf - DXF writer instance
 * @param xAxisStart - X-axis start position
 * @param xAxisEnd - X-axis end position
 * @param yAxisStart - Y-axis start position
 * @param yAxisEnd - Y-axis end position
 */
const drawAxisLabels = (
  dxf: DXFWriter,
  xAxisStart: number,
  xAxisEnd: number,
  yAxisStart: number,
  yAxisEnd: number,
): void => {
  const { xAxis: xAxisLabel, yAxis: yAxisLabel } = axisProperties.label;
  const {
    axisLabelTextSize,
    axisLabelOffsetX,
    axisLabelOffsetY,
    axisLabelRotation,
  } = dxfConstants;

  const xAxisCenter = (xAxisStart + xAxisEnd) / 2;
  const yAxisCenter = (yAxisStart + yAxisEnd) / 2;

  // X-axis label.
  dxf.drawText(
    xAxisCenter,
    yAxisStart + axisLabelOffsetY,
    axisLabelTextSize,
    0,
    xAxisLabel,
    DxfAxisLabelAlignment.Center,
    DxfAxisLabelAlignment.Top,
  );

  // Y-axis label.
  dxf.drawText(
    xAxisStart + axisLabelOffsetX,
    yAxisCenter,
    axisLabelTextSize,
    axisLabelRotation,
    yAxisLabel,
    DxfAxisLabelAlignment.Center,
    DxfAxisLabelAlignment.Middle,
  );
};

/**
 * Calculates the point size in coordinate units.
 * @param dataRange - Maximum data range
 * @param pointSize - Point size in pixels (optional)
 * @returns Point size in coordinate units
 */
const calculatePointSize = (dataRange: number, pointSize?: number): number => {
  const { defaultPointSizePercent, pointSizeScaleFactor, pointSizeMultiplier } =
    dxfConstants;

  const defaultPointSize = dataRange * defaultPointSizePercent;

  if (isNil(pointSize)) {
    return defaultPointSize;
  }

  return (pointSize / pointSizeScaleFactor) * (dataRange * pointSizeMultiplier);
};

/**
 * Draws points as layers grouped by color.
 * @param dxf - DXF writer instance
 * @param colorGroups - Map of colors to point groups
 * @param halfSize - Half of the point size
 * @param pointShape - Shape of the points (circle or square)
 */
const drawPointLayers = (
  dxf: DXFWriter,
  colorGroups: Map<string, DxfLayerPoint[]>,
  halfSize: number,
  pointShape: PointShape,
): void => {
  const { pointLayerColor, gridlineLineType, pointLayerNamePrefix } =
    dxfConstants;

  let layerIndex = 0;

  colorGroups.forEach((pointGroup, colorKey) => {
    const [r, g, b] = colorKey.split(',').map(Number);
    const trueColor = rgbToDxfColor(r, g, b);
    const layerName = `${pointLayerNamePrefix}-${layerIndex}`;

    const pointLayer = dxf.addLayer(
      layerName,
      pointLayerColor,
      gridlineLineType,
    );
    dxf.setActiveLayer(layerName);
    pointLayer.setTrueColor(trueColor);

    // Draw each point in this color group as circle or square.
    pointGroup.forEach((point) => {
      if (pointShape === PointShape.Square) {
        // Draw square using rectangle.
        dxf.drawRect(
          point.x - halfSize,
          point.y - halfSize,
          point.x + halfSize,
          point.y + halfSize,
        );
      } else {
        // Draw circle.
        dxf.drawCircle(point.x, point.y, halfSize);
      }
    });

    layerIndex++;
  });
};

/**
 * Generates a sanitized filename for the DXF export.
 * @param selectedPointCloudLayer - Selected point cloud layer
 * @returns Sanitized filename
 */
const generateFilename = (selectedPointCloudLayer: WorkspaceLayer): string => {
  const siteName = selectedPointCloudLayer.site?.name ?? 'unknown-site';
  const iterationName =
    selectedPointCloudLayer.iteration?.name ?? 'unknown-iteration';

  return `cross-section_${siteName}_${iterationName}.dxf`;
};

/**
 * Downloads cross-section profile as DXF with individual RGB colors per point.
 * @param data - Cross-section point data
 * @param selectedPointCloudLayer - Selected point cloud layer
 */
export const downloadAsDXF = (
  data: CrossSectionPointData,
  selectedPointCloudLayer: WorkspaceLayer,
): void => {
  try {
    // Validate input data.
    if (isNil(data) || data.count === 0) {
      toast.error('No data available to download.');
      return;
    }

    if (isNil(selectedPointCloudLayer)) {
      toast.error('No point cloud layer selected.');
      return;
    }

    // Initialize DXF writer.
    const dxf = new DXFWriter();
    dxf.setUnits('Meters');

    // Extract points and calculate bounds.
    const { points, bounds } = extractPointsAndBounds(data);

    if (isEmpty(points)) {
      toast.error('No valid points to export.');
      return;
    }

    // Create gridlines layer.
    createGridlinesLayer(dxf);

    // Calculate axis bounds with padding.
    const { axisPadding } = dxfConstants;
    const xAxisStart = bounds.minX - axisPadding;
    const xAxisEnd = bounds.maxX + axisPadding;
    const yAxisStart = bounds.minY - axisPadding;
    const yAxisEnd = bounds.maxY + axisPadding;

    // Draw gridlines and labels.
    drawGridlinesAndLabels(dxf, xAxisStart, xAxisEnd, yAxisStart, yAxisEnd);

    // Draw bounding rectangle.
    dxf.drawRect(xAxisStart, yAxisStart, xAxisEnd, yAxisEnd);

    // Draw axis labels.
    drawAxisLabels(dxf, xAxisStart, xAxisEnd, yAxisStart, yAxisEnd);

    // Calculate data range and point size.
    const dataRangeX = bounds.maxX - bounds.minX;
    const dataRangeY = bounds.maxY - bounds.minY;
    const dataRange = Math.max(dataRangeX, dataRangeY);

    // Get point cloud properties.
    const pointCloudProperties = getPointCloudProperties(
      selectedPointCloudLayer,
    );
    const { pointSize, pointShape } = pointCloudProperties;

    // Calculate point size in coordinate units.
    const pointSizeInUnits = calculatePointSize(dataRange, pointSize);
    const halfSize = pointSizeInUnits / 2;

    // Group points by color and draw layers.
    const colorGroups = groupPointsByColor(points);
    drawPointLayers(dxf, colorGroups, halfSize, pointShape);

    // Generate DXF string and save.
    const output = dxf.toDxfString();

    if (isNil(output) || isEmpty(output)) {
      toast.error('There was an error in generating the DXF file.');
      return;
    }

    const blob = new Blob([output], { type: 'application/dxf' });
    const filename = generateFilename(selectedPointCloudLayer);
    FileSaver.saveAs(blob, filename);
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('[CrossSection] DXF download failed:', {
      error: errorMessage,
      dataCount: data?.count,
      layerId: selectedPointCloudLayer?.id,
    });
    toast.error(`Download failed: ${errorMessage}. Please try again.`);
  }
};
